import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stethoscope, Upload, ShieldCheck, ArrowRight, Loader2, Check } from "lucide-react";
import SEO from "@/components/SEO";

/**
 * Clinician Trial Wizard — 30-day FREE trial for healthcare professionals.
 * Required fields (user-confirmed Iter 257, option b):
 *   1. License number (UIN — Uniform Identification Number)
 *   2. Specialty
 *   3. License document upload (skan / медицинско удостоверение)
 *
 * Always requires admin review (option a) — 24h SLA before activation.
 */
export default function ClinicianTrialApplyPage() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    license_number: "",
    specialty: "",
    document_file: null,
    document_url: "",
    notes: "",
  });

  const SPECIALTIES = bg
    ? ["Ендокринология", "Диетология / Хранене", "Гастроентерология", "Гинекология", "Кардиология", "Генетика", "Обща практика", "Друго"]
    : ["Endocrinology", "Dietetics / Nutrition", "Gastroenterology", "Gynecology", "Cardiology", "Genetics", "General Practice", "Other"];

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(bg ? "Файлът е твърде голям (макс 10MB)" : "File too large (max 10MB)");
      return;
    }
    setForm((f) => ({ ...f, document_file: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.license_number.trim() || !form.specialty.trim() || !form.document_file) {
      toast.error(bg
        ? "Моля попълнете УИН, специалност и качете документ"
        : "Please fill license number, specialty, and upload document");
      return;
    }
    setSubmitting(true);
    try {
      // Upload document first
      const fd = new FormData();
      fd.append("file", form.document_file);
      const upload = await axios.post(`${API}/admin/upload-document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }).catch(() => null);
      const documentUrl = upload?.data?.url || `clinician_doc_${Date.now()}_${form.document_file.name}`;

      await axios.post(`${API}/usage/trial/start-clinician`, {
        license_number: form.license_number.trim(),
        specialty: form.specialty.trim(),
        license_document_url: documentUrl,
        notes: form.notes.trim() || null,
      });
      setSubmitted(true);
      toast.success(bg
        ? "Заявката е получена! Очаквайте email до 24 часа."
        : "Request received! Expect an email within 24 hours.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка при подаване" : "Submission error"));
    } finally {
      setSubmitting(false);
    }
  };

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const cardBg = isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100";

  if (submitted) {
    return (
      <div className={`min-h-screen ${sectionBg} flex items-center justify-center p-4`} data-testid="clinician-trial-success">
        <div className={`max-w-md w-full p-8 rounded-2xl border ${cardBg} text-center`}>
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
            <Check className="w-7 h-7 text-emerald-500" />
          </div>
          <h1 className={`text-xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Заявката е получена" : "Request received"}
          </h1>
          <p className={`text-sm mb-6 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg
              ? "Прегледахме заявката Ви и ще получите email с решението в рамките на 24 часа. Междувременно можете да използвате пациентския trial."
              : "We've received your application and will email you the decision within 24 hours. Meanwhile, you can use the patient trial."}
          </p>
          <Button onClick={() => navigate("/dashboard")} className="w-full" data-testid="back-to-dashboard-btn">
            {bg ? "Към таблото" : "Go to Dashboard"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${sectionBg} py-8 px-4`} data-testid="clinician-trial-apply-page">
      <SEO title={bg ? "Заявка за Clinician trial" : "Clinician trial application"} path="/clinician-trial-apply" />
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "30-дневен Clinician trial" : "30-day Clinician trial"}
          </h1>
          <p className={`text-sm max-w-md mx-auto ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg
              ? "Безплатен пълен достъп до клиничните инструменти (KEGG, Phenotype Synergy, дозировки) — без кредитна карта. Изисква проверка на медицински документ за GDPR/EU AI Act compliance."
              : "Free full access to clinical tools (KEGG, Phenotype Synergy, dosages) — no credit card. Requires medical document verification for GDPR/EU AI Act compliance."}
          </p>
        </div>

        {/* Compliance banner */}
        <div className={`rounded-lg p-3 mb-5 border text-[11.5px] flex items-start gap-2 ${
          isDark ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-100" : "bg-emerald-50 border-emerald-200 text-emerald-900"
        }`} data-testid="compliance-banner">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
          <span>
            {bg
              ? "Документите се преглеждат ръчно от admin team в рамките на 24 часа. Достъпът се активира едва след одобрение. Това гарантира спазването на EU AI Act и FDA изискванията — клиничните инструменти са достъпни само за здравни специалисти."
              : "Documents are manually reviewed by our admin team within 24h. Access is activated only after approval — ensuring EU AI Act + FDA compliance (clinical tools restricted to healthcare professionals)."}
          </span>
        </div>

        {/* Form */}
        <form onSubmit={submit} className={`p-6 rounded-2xl border ${cardBg} space-y-4`}>
          {/* License number */}
          <div>
            <Label htmlFor="license_number" className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
              {bg ? "УИН (Уникален идентификационен номер)" : "License number (UIN)"} *
            </Label>
            <Input
              id="license_number"
              type="text"
              value={form.license_number}
              onChange={(e) => setForm({ ...form, license_number: e.target.value })}
              placeholder={bg ? "напр. 12345-67890" : "e.g. 12345-67890"}
              required
              data-testid="license-number-input"
              className={`mt-1.5 ${isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white" : ""}`}
            />
          </div>

          {/* Specialty */}
          <div>
            <Label htmlFor="specialty" className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
              {bg ? "Специалност" : "Specialty"} *
            </Label>
            <select
              id="specialty"
              value={form.specialty}
              onChange={(e) => setForm({ ...form, specialty: e.target.value })}
              required
              data-testid="specialty-select"
              className={`mt-1.5 w-full h-10 px-3 rounded-md border text-sm ${
                isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white" : "bg-white border-slate-200"
              }`}
            >
              <option value="">{bg ? "— Изберете —" : "— Select —"}</option>
              {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Document upload */}
          <div>
            <Label className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
              {bg ? "Скан на диплома / медицинско удостоверение" : "Diploma / medical license scan"} *
            </Label>
            <label
              htmlFor="document_file"
              className={`mt-1.5 flex items-center gap-2 px-3 py-2.5 rounded-md border-2 border-dashed cursor-pointer transition-colors ${
                form.document_file
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : isDark ? "border-slate-700 hover:border-rose-500/40" : "border-slate-200 hover:border-rose-300"
              }`}
              data-testid="document-upload-label"
            >
              <Upload className={`w-4 h-4 ${form.document_file ? "text-emerald-500" : isDark ? "text-slate-400" : "text-slate-500"}`} />
              <span className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"} flex-1 truncate`}>
                {form.document_file
                  ? `${form.document_file.name} (${(form.document_file.size / 1024).toFixed(0)} KB)`
                  : (bg ? "Кликнете за избор на PDF / JPG / PNG (макс 10MB)" : "Click to select PDF / JPG / PNG (max 10MB)")}
              </span>
            </label>
            <input
              id="document_file"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
              data-testid="document-upload-input"
            />
            <p className={`text-[10.5px] mt-1.5 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              {bg
                ? "Файлът се съхранява криптиран и се изтрива автоматично 30 дни след одобрение."
                : "Document is encrypted at rest and auto-deleted 30 days after approval."}
            </p>
          </div>

          {/* Optional notes */}
          <div>
            <Label htmlFor="notes" className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
              {bg ? "Допълнителна информация (по избор)" : "Additional notes (optional)"}
            </Label>
            <textarea
              id="notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={bg ? "Място на работа, защо искате trial..." : "Workplace, why you want a trial..."}
              rows={3}
              data-testid="notes-input"
              className={`mt-1.5 w-full px-3 py-2 rounded-md border text-sm ${
                isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,18%)] text-white" : "bg-white border-slate-200"
              }`}
            />
          </div>

          {/* Submit */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              data-testid="cancel-btn"
              className="flex-1"
            >
              {bg ? "Отказ" : "Cancel"}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-clinician-trial-btn"
              className="flex-1 bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white font-semibold"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {bg ? "Изпращане..." : "Submitting..."}</>
              ) : (
                <>{bg ? "Подай заявка" : "Submit application"} <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
